// TUS DÍAS — qué día de la semana te deja más plata, y qué días se salieron de lo normal.
//
// Este bloque existe para corregir un error concreto de la pantalla vieja.
// `Stats/TabResumen.js:204` hace `slice(0, 30)` y promedia el `income` por día
// de la semana **sin filtrar nada**. Un estacionamiento que no abre los domingos
// tiene ahí cuatro ceros en la muestra del domingo, y el promedio le dice que el
// domingo es su peor día. No es su peor día: es el día que no abre. Con esa
// cuenta, el que cerró dos martes por un feriado ve que "los martes bajaron".
//
// Acá pasan tres cosas distintas, y las tres son la razón de ser del archivo:
//
// 1. MEDIANA, NUNCA PROMEDIO. Un sábado con un evento al lado corre el promedio
//    de los sábados para todo el trimestre y deja la vara mal puesta. La mediana
//    —el valor del medio— no se mueve.
//
// 2. LOS DÍAS SIN OPERAR NO ENTRAN. Ni al cálculo, ni al gráfico. Un día cerrado
//    no vale $0: vale *nada*, y son cosas distintas. Los días de la semana en
//    los que el cliente habitualmente no abre ni siquiera dibujan barra, porque
//    una barra en cero se lee como "ese día no vendo" y lo cierto es "ese día no
//    estoy".
//
// 3. EN PLATA, NO EN AUTOS. El dueño decide con plata. Un domingo de 40 autos
//    por hora puede dejarle menos que un martes de 25 con estadías largas.
//
// Debajo del gráfico va "Días para revisar": como máximo cinco filas, sólo los
// días que se salieron de la banda habitual de SU PROPIO día de la semana. Eso
// reemplaza a las dos tablas de 30 filas de la pantalla vieja, que obligaban a
// comparar a ojo un martes contra un sábado.

import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { SIN_LEYENDA } from "../Stats/graficos";
import Bloque from "./Bloque";
import { ORIGEN, UI, POLARIDAD, ESTADO } from "./theme";
import { formatCurrency, fecha, entero } from "./format";
import { getCurrencySymbol } from "../../utils/formatCurrency";
// El patrón de la semana ya no se calcula acá: vive en `baseline.js` y lo
// consume también el resumen de "Lo que salta a la vista". Son el mismo jueves,
// así que tienen que ser el mismo número — y la única forma de garantizarlo es
// que no exista una segunda cuenta que pueda divergir.
import {
  DIAS_CORTO,
  DIAS_LARGO,
  MIN_HISTORIA,
  MIN_MUESTRAS,
  ORDEN_SEMANA,
  dowDe,
  enPlural,
  isoLocal,
  noOpero,
  patronSemanal,
} from "./baseline";

// ── Constantes de método ─────────────────────────────────────────────────
//
// Las que gobiernan el patrón —la ventana de 90 días, los mínimos de historia y
// de muestras— están en `baseline.js`, porque cambiarlas cambia también lo que
// afirma el resumen. Acá quedan sólo las de la auditoría fila por fila, que no
// sale de esta pantalla:
const REVISAR_DIAS = 30;   // qué ventana se audita fila por fila
const MAX_FILAS = 5;       // más de cinco cosas para revisar no se revisa ninguna

// Gris de "no hay dato". Pertenece a `theme.NEUTRO.sinDatos` según la
// especificación, pero `theme.js` todavía no lo exporta y este archivo no puede
// tocarlo. Cuando NEUTRO exista, esta constante se borra y se importa.
// NO es un color elegido por gusto: es un neutro, y su función es que "no sé"
// nunca se confunda con "entra" ni con "sale".
const SIN_DATOS = "#d8d6d1";

const AZUL = ORIGEN.tickets.color;

// ── Utilidades locales ───────────────────────────────────────────────────
//
// Sólo las que no salen de esta pantalla. Todo lo que define "lo normal" de un
// día de la semana —la mediana, la banda, qué días no abre el cliente— se mudó
// a `baseline.js` el día que el resumen empezó a afirmar lo mismo que este
// bloque.

/**
 * Plata abreviada, SÓLO para ejes y etiquetas de barra.
 *
 * En un texto nunca: "$ 4,2 M" en una frase es pedirle al lector que traduzca.
 * El símbolo se pide a `getCurrencySymbol()` y no se escribe "$" acá, porque el
 * Monitor se usa en 12 países y el símbolo lo setea el listener según el cliente.
 */
const plataCorta = (v) => {
  const n = Number(v) || 0;
  const s = getCurrencySymbol();
  const abs = Math.abs(n);
  const coma = (x, dec) => x.toFixed(dec).replace(".", ",").replace(/,0$/, "");
  if (abs >= 1e6) return `${s} ${coma(n / 1e6, 1)} M`;
  if (abs >= 1e5) return `${s} ${coma(n / 1e3, 0)} k`;
  if (abs >= 1e3) return `${s} ${coma(n / 1e3, 1)} k`;
  return `${s} ${Math.round(n)}`;
};

/** Une con comas y una "y" final, como lo escribiría una persona. */
const enumerar = (partes) => {
  if (partes.length <= 1) return partes[0] || "";
  return `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
};

// ── El titular ───────────────────────────────────────────────────────────
//
// Ningún número aparece solo: la conclusión es la distancia entre el mejor día y
// el peor, dicha en palabras. "Casi el doble" es una afirmación que se puede
// verificar mirando el gráfico; "$312.000 los sábados" no concluye nada.
const fraseTitular = (mejor, peor) => {
  const a = DIAS_LARGO[mejor.dow].toLowerCase();
  const b = DIAS_LARGO[peor.dow].toLowerCase();
  if (!peor.mediana || peor.mediana <= 0) {
    return `El ${a} es, de lejos, el día que más te deja`;
  }
  const r = mejor.mediana / peor.mediana;
  const pct = Math.round((r - 1) * 100);
  if (r >= 2.2) return `El ${a} te deja más del doble que el ${b}`;
  if (r >= 1.75) return `El ${a} te deja casi el doble que el ${b}`;
  if (r >= 1.15) return `El ${a} te deja ${pct}% más que el ${b}`;
  return `Tu semana es pareja: entre el ${a} y el ${b} hay ${pct}% de diferencia`;
};

// ── El gráfico ───────────────────────────────────────────────────────────

const useEsMovil = () => {
  const [esMovil, setEsMovil] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const alRedimensionar = () => setEsMovil(window.innerWidth < 768);
    window.addEventListener("resize", alRedimensionar);
    return () => window.removeEventListener("resize", alRedimensionar);
  }, []);
  return esMovil;
};

// ── Días para revisar ────────────────────────────────────────────────────

const Chip = ({ color, rayado }) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: 8,
      height: 26,
      borderRadius: 3,
      flexShrink: 0,
      background: rayado
        ? `repeating-linear-gradient(45deg, ${color}, ${color} 3px, transparent 3px, transparent 6px)`
        : color,
      border: rayado ? `1px solid ${color}` : "none",
    }}
  />
);

const Fila = ({ color, rayado, cuando, monto, nota }) => (
  // En el teléfono el monto y la nota bajan debajo de la fecha: en 320 px las
  // tres columnas parten el texto en pedazos de dos palabras.
  <div
    className="d-flex align-items-start gap-2"
    style={{ padding: "8px 0", borderBottom: `1px solid ${UI.linea}` }}
  >
    <Chip color={color} rayado={rayado} />
    <div className="d-flex flex-column flex-sm-row gap-1" style={{ flex: 1, minWidth: 0 }}>
      <div style={{ flex: "0 0 130px", fontSize: 13, color: UI.texto, fontWeight: 600 }}>{cuando}</div>
      <div style={{ flex: "0 0 auto", minWidth: 110, fontSize: 13.5, fontWeight: 700, color: UI.texto }}>
        {monto}
      </div>
      <div style={{ flex: "1 1 auto", fontSize: 12.5, color: UI.textoSuave, lineHeight: 1.4 }}>{nota}</div>
    </div>
  </div>
);

/**
 * Audita los últimos 30 días, fecha por fecha (no documento por documento).
 *
 * Recorrer los documentos saltea los días que NO llegaron, que son justamente
 * los que hay que reportar. Un mes con seis días caídos y un mes completo tienen
 * la misma cantidad de documentos consecutivos si uno mira el array.
 */
const diasParaRevisar = (docs, base, dowCerrados, primerDia) => {
  const porFecha = {};
  docs.forEach((d) => { if (d?.date) porFecha[d.date] = d; });

  // Termina ayer: `cerrados` no incluye el día en curso, y un día a medio
  // transcurrir siempre queda por debajo de su banda. Reportarlo sería una
  // alarma falsa todos los días a la mañana.
  const fin = new Date();
  fin.setHours(0, 0, 0, 0);
  fin.setDate(fin.getDate() - 1);

  const filas = [];
  for (let i = REVISAR_DIAS - 1; i >= 0; i--) {
    const d = new Date(fin);
    d.setDate(d.getDate() - i);
    const iso = isoLocal(d);
    // Antes del primer día del cliente no hay nada que reportar: no es un hueco,
    // es que todavía no existía.
    if (primerDia && iso < primerDia) continue;

    const dow = dowDe(iso);
    const doc = porFecha[iso];
    const cuando = `${DIAS_CORTO[dow].toLowerCase()} ${fecha(iso)}`;

    if (!doc) {
      // Si ese día de la semana no abre, la ausencia es lo esperado.
      if (dowCerrados.has(dow)) continue;
      filas.push({
        orden: 0,
        peso: Number.MAX_SAFE_INTEGER,
        color: SIN_DATOS,
        rayado: true,
        cuando,
        monto: "sin datos",
        nota: "ese día no llegó ningún dato al Monitor, y no entra en ningún promedio de esta pantalla",
      });
      continue;
    }

    const income = Number(doc.income) || 0;
    const entradas = Number(doc?.tickets?.total?.in) || 0;

    if (income === 0 && entradas > 0) {
      filas.push({
        orden: 0,
        peso: Number.MAX_SAFE_INTEGER - 1,
        color: ESTADO.anulado.color,
        rayado: false,
        cuando,
        monto: formatCurrency(0),
        nota: `entraron ${entero(entradas)} vehículos y no se registró ningún cobro`,
      });
      continue;
    }

    if (noOpero(doc)) {
      if (dowCerrados.has(dow)) continue;   // domingo cerrado en un local que no abre los domingos
      filas.push({
        orden: 1,
        peso: Number.MAX_SAFE_INTEGER - 2,
        color: SIN_DATOS,
        rayado: true,
        cuando,
        monto: formatCurrency(0),
        nota: `no hubo movimiento, y ${enPlural(dow)} normalmente abres`,
      });
      continue;
    }

    // Día normal: se compara contra la banda de SU día de la semana, no contra
    // el resto de la semana. Un martes flojo no es un sábado flojo.
    const b = base[dow];
    if (!b || b.p25 === null || b.p75 === null || !b.mediana) continue;
    if (income >= b.p25 && income <= b.p75) continue;

    const arriba = income > b.p75;
    const pct = Math.round(((income - b.mediana) / b.mediana) * 100);
    filas.push({
      orden: 2,
      peso: Math.abs(income - b.mediana),
      color: arriba ? POLARIDAD.entra.color : POLARIDAD.sale.color,
      rayado: false,
      cuando,
      monto: formatCurrency(income),
      nota: `${arriba ? "+" : "−"}${Math.abs(pct)}% para ser un ${DIAS_LARGO[dow].toLowerCase()}`,
    });
  }

  // Primero lo que impide cobrar o medir, después lo que más plata mueve.
  filas.sort((a, b) => a.orden - b.orden || b.peso - a.peso);
  return filas;
};

const DiasParaRevisar = ({ filas }) => (
  <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${UI.linea}` }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: UI.texto }}>Días para revisar</div>
    <p style={{ fontSize: 12.5, color: UI.textoSuave, margin: "2px 0 8px", lineHeight: 1.45 }}>
      De los últimos 30 días, sólo los que se salieron de lo habitual para ese mismo día de la semana.
    </p>

    {filas.length === 0 ? (
      <div style={{ fontSize: 13.5, color: UI.texto }}>Ningún día se salió de lo normal.</div>
    ) : (
      <>
        {filas.slice(0, MAX_FILAS).map((f) => (
          <Fila
            key={f.cuando}
            color={f.color}
            rayado={f.rayado}
            cuando={f.cuando}
            monto={f.monto}
            nota={f.nota}
          />
        ))}
        {filas.length > MAX_FILAS && (
          // Se cortan en cinco a propósito: una lista de veinte "días para
          // revisar" no la revisa nadie, y deja de ser una lista de excepciones.
          <div style={{ fontSize: 12.5, color: UI.textoSuave, marginTop: 8 }}>
            Hay {filas.length - MAX_FILAS} día{filas.length - MAX_FILAS === 1 ? "" : "s"} más fuera de lo
            habitual. Se muestran los cinco que más mueven la aguja.
          </div>
        )}
      </>
    )}
  </div>
);

// ── El bloque ────────────────────────────────────────────────────────────

// Va por `Bloque` y el titular dice qué falta, no cómo se llama el bloque. "Tus
// días" sobre un párrafo gris obliga a leer las tres líneas de abajo para
// enterarse de que el patrón todavía no existe.
// Dos motivos distintos llegan acá: poca historia, o historia suficiente pero
// repartida en menos de tres días de la semana medidos. El texto tiene que servir
// para los dos: prometerle "en un mes sí" a alguien que ya tiene noventa días es
// una fecha que no se va a cumplir.
const Vacio = ({ dias }) => (
  <Bloque
    nombre="Tus días"
    id="tus-dias"
    titular={
      dias === 0
        ? "Todavía no hay jornadas cerradas para ver el patrón de tu semana"
        : dias < MIN_HISTORIA
          ? `Con ${dias} día${dias === 1 ? "" : "s"} de historia todavía no se ve el patrón de tu semana`
          : "Todavía no hay suficientes días medidos para ver el patrón de tu semana"
    }
    frase={
      dias < MIN_HISTORIA
        ? `El patrón sale de comparar cada día de la semana contra sí mismo, y para eso hacen falta `
          + `${MIN_HISTORIA} días con la jornada cerrada. Se completa solo: cada jornada que `
          + "TicketControl cierra suma una muestra, no hay que hacer nada."
        : `Hacen falta al menos tres días de la semana con ${MIN_MUESTRAS} jornadas medidas cada uno, `
          + "y todavía no los hay. Se completa solo: cada jornada que TicketControl cierra suma una "
          + "muestra, no hay que hacer nada."
    }
  />
);

const TusDias = ({ cerrados = [] }) => {
  const esMovil = useEsMovil();

  const datos = useMemo(() => {
    // El patrón sale entero de `baseline.js`. Este bloque no vuelve a calcular
    // ni la mediana de cada día ni qué días no abre el cliente: si lo hiciera,
    // el gráfico y el resumen podrían mostrar dos jueves distintos.
    const patron = patronSemanal(cerrados);
    if (!patron.suficiente) return null;

    const { base, dowCerrados } = patron;

    return {
      ...patron,
      barras: ORDEN_SEMANA.map((dow) => {
        const b = base[dow];
        const cerrado = dowCerrados.has(dow);
        // Un día que no abre NO lleva barra en cero. Una barra en cero se lee
        // "ese día no vendo"; lo cierto es "ese día no estoy".
        const y = cerrado || !b || b.n < MIN_MUESTRAS ? null : Math.round(b.mediana);
        return { dow, y, cerrado, n: b ? b.n : 0, p25: b ? b.p25 : null, p75: b ? b.p75 : null };
      }),
      revisar: diasParaRevisar(cerrados, base, dowCerrados, cerrados[0]?.date),
    };
  }, [cerrados]);

  if (!datos) return <Vacio dias={Array.isArray(cerrados) ? cerrados.length : 0} />;

  const categorias = datos.barras.map((b) => (esMovil ? DIAS_CORTO[b.dow] : DIAS_LARGO[b.dow]));
  const serie = [{ name: "Lo habitual de ese día", data: datos.barras.map((b) => b.y) }];

  // 7 puntos entran en escritorio; en teléfono se pisan sin remedio. Es la regla
  // `etiquetasSiCaben`: en el teléfono devuelve false casi siempre, y es a
  // propósito — el tooltip sigue estando.
  const etiquetas = !esMovil;

  const options = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
    // Un solo color para las siete barras: son el mismo concepto y sólo cambia
    // la magnitud. Siete colores distintos serían color por posición, que es
    // exactamente lo que theme.js existe para impedir.
    colors: [AZUL],
    plotOptions: { bar: { columnWidth: esMovil ? "62%" : "52%", borderRadius: 4, dataLabels: { position: "top" } } },
    dataLabels: {
      enabled: etiquetas,
      formatter: (v) => (v === null || v === undefined ? "" : plataCorta(v)),
      offsetY: -20,
      style: { fontSize: "11px", fontWeight: 600, colors: [UI.textoSuave] },
    },
    legend: SIN_LEYENDA,   // una sola serie y un solo color: el subtítulo ya la nombra
    grid: { borderColor: UI.linea, strokeDashArray: 3, padding: { left: 4, right: 4, top: etiquetas ? 12 : 0 } },
    xaxis: {
      categories: categorias,
      labels: { style: { colors: UI.textoSuave, fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: UI.textoSuave, fontSize: "11px" }, formatter: (v) => plataCorta(v) },
    },
    tooltip: {
      y: {
        formatter: (v, { dataPointIndex }) => {
          const b = datos.barras[dataPointIndex];
          // Sin esta guarda, un dataPointIndex fuera de rango llegaba a `b.n`
          // y tiraba el tooltip entero con un TypeError.
          if (!b) return "—";
          if (b.cerrado) return "no abres ese día";
          if (v === null || v === undefined) return "todavía sin suficientes días medidos";
          return `${formatCurrency(v)} · ${b.n} día${b.n === 1 ? "" : "s"} medidos`;
        },
      },
    },
  };

  const cerradosTexto = ORDEN_SEMANA.filter((d) => datos.dowCerrados.has(d)).map(enPlural);
  const pocasTexto = datos.pocasMuestras.map(enPlural);

  return (
    <Bloque
      nombre="Tus días"
      id="tus-dias"
      titular={fraseTitular(datos.mejor, datos.peor)}
      frase="Es el valor del medio de cada día de la semana, no el promedio: así un día excepcional no corre la vara. Sirve para decidir qué día reforzar el personal y cuál promocionar."
      rango={`${fecha(datos.desde)} – ${fecha(datos.hasta)} · ${datos.diasOperados} días con movimiento`}
      acento={AZUL}
      detalleLabel="Ver los números de cada día"
      detalle={
        <table style={{ width: "100%", fontSize: 12.5, color: UI.texto }}>
          <thead>
            <tr style={{ color: UI.textoSuave, textAlign: "left" }}>
              <th style={{ padding: "4px 8px 4px 0", fontWeight: 500 }}>Día</th>
              <th style={{ padding: "4px 8px", fontWeight: 500 }}>Lo habitual</th>
              <th style={{ padding: "4px 8px", fontWeight: 500 }}>Entre</th>
              <th style={{ padding: "4px 0 4px 8px", fontWeight: 500 }}>Días medidos</th>
            </tr>
          </thead>
          <tbody>
            {datos.barras.map((b) => (
              <tr key={b.dow} style={{ borderTop: `1px solid ${UI.linea}` }}>
                <td style={{ padding: "6px 8px 6px 0" }}>{DIAS_LARGO[b.dow]}</td>
                <td style={{ padding: "6px 8px", fontWeight: 600 }}>
                  {b.cerrado ? "no abres" : b.y === null ? "sin medir" : formatCurrency(b.y)}
                </td>
                <td style={{ padding: "6px 8px", color: UI.textoSuave }}>
                  {b.p25 === null || b.p75 === null
                    ? "—"
                    : `${formatCurrency(Math.round(b.p25))} y ${formatCurrency(Math.round(b.p75))}`}
                </td>
                <td style={{ padding: "6px 0 6px 8px", color: UI.textoSuave }}>{b.cerrado ? "—" : b.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <ReactApexChart options={options} series={serie} type="bar" height={esMovil ? 230 : 280} />

      {/* Por qué faltan barras. Sin esta línea, un hueco en el gráfico se lee
          como un error de la pantalla. */}
      {(cerradosTexto.length > 0 || pocasTexto.length > 0) && (
        <div style={{ fontSize: 12, color: UI.textoSuave, marginTop: 6, lineHeight: 1.5 }}>
          {cerradosTexto.length > 0 && (
            <div>
              {enumerar(cerradosTexto).replace(/^./, (c) => c.toUpperCase())} no aparecen: esos días no
              abres, y contarlos como cero hundiría el promedio del día que te tocó estar cerrado.
            </div>
          )}
          {pocasTexto.length > 0 && (
            <div>
              {enumerar(pocasTexto).replace(/^./, (c) => c.toUpperCase())} todavía no tienen suficientes
              días medidos para decir cuánto te dejan.
            </div>
          )}
        </div>
      )}

      <DiasParaRevisar filas={datos.revisar} />
    </Bloque>
  );
};

export { DiasParaRevisar };
export default TusDias;
